import React, { useEffect } from 'react';
import { Form, Input, Select } from 'antd';
import EditableDrawer from '../../../../components/EditableDrawer';
import { WarehouseOption } from '../../../../hooks/useWarehouseOptions';
import { useTranslation } from 'react-i18next';
import { HandoverCarrierType } from '../../../../graphql/types/HandoverCarrierType';
import { ALL_CENTER_WAREHOUSE_ID } from '../../../../enums/TmsEnum';

interface HandoverCarrierFormDrawerProps {
  warehousesOptions: WarehouseOption[];
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: HandoverCarrierType) => void;
  carrier: HandoverCarrierType | null;
  mode: 'create' | 'edit';
}

const HandoverCarrierFormDrawer: React.FC<HandoverCarrierFormDrawerProps> = ({
  warehousesOptions,
  visible,
  onClose,
  onSubmit,
  carrier,
  mode,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const title =
    mode === 'create'
      ? t('central.handoverCarrier.pageDetails.new')
      : t('central.handoverCarrier.pageDetails.details');

  const warehouseOptions = (() => {
    const hasNationwide = warehousesOptions.some((w) => String(w.id) === ALL_CENTER_WAREHOUSE_ID);
    const extended = hasNationwide
      ? warehousesOptions
      : [{ id: ALL_CENTER_WAREHOUSE_ID, name: t('common.allCenterWarehouse') }, ...warehousesOptions];
    return extended.map((w) => ({ value: String(w.id), label: w.name }));
  })();

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (carrier) {
        const centerWarehouseIds = Array.isArray(carrier.centerWarehouseIds)
          ? carrier.centerWarehouseIds
          : carrier.centerWarehouseIds != null
          ? [carrier.centerWarehouseIds]
          : [];
        form.setFieldsValue({ ...carrier, centerWarehouseIds });
      } else {
        form.setFieldsValue({ status: 1, centerWarehouseIds: [] });
      }
    }
  }, [visible, carrier, mode, form]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (mode === 'create') {
          const { id, ...submitValues } = values;
          void id;
          onSubmit(submitValues as HandoverCarrierType);
          return;
        }
        onSubmit(values);
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <EditableDrawer
      title={title}
      onClose={onClose}
      visible={visible}
      width={600}
      onSubmit={handleSubmit}
    >
      <Form
        form={form}
        layout="horizontal"
        labelAlign="left"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        {mode === 'edit' && (
          <>
            <Form.Item name="id" hidden={true}>
              <Input />
            </Form.Item>
            <Form.Item label={t('central.handoverCarrier.pageDetails.id')}>
              <Input
                disabled={true}
                value={carrier?.id ? `H${String(carrier.id).padStart(5, '0')}` : '-'}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="carrierName"
          label={t('central.handoverCarrier.pageDetails.carrierName')}
          rules={[{ required: true, message: t('central.handoverCarrier.messages.pleaseInputCarrierName') as string }]}
        >
          <Input placeholder={t('central.handoverCarrier.placeholders.enterCarrierName') as string} maxLength={50} showCount />
        </Form.Item>

        <Form.Item
          name="shortCarrierName"
          label={t('central.handoverCarrier.pageDetails.shortCarrierName')}
          rules={[{ required: true, message: t('central.handoverCarrier.messages.pleaseInputShortCarrierName') as string }]}
        >
          <Input placeholder={t('central.handoverCarrier.placeholders.enterShortCarrierName') as string} maxLength={50} showCount />
        </Form.Item>

        <Form.Item
          name="carrierType"
          label={t('central.handoverCarrier.pageDetails.carrierType')}
          rules={[{ required: true, message: t('central.handoverCarrier.messages.pleaseSelectCarrierType') as string }]}
        >
          <Select
            placeholder={t('central.handoverCarrier.placeholders.selectCarrierType') as string}
            disabled={mode === 'edit'}
            options={[
              { value: 1, label: t('central.handoverCarrier.types.localLastMile') },
              { value: 2, label: t('central.handoverCarrier.types.linehaul') },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="contactName"
          label={t('central.handoverCarrier.pageDetails.contactName')}
          rules={[{ required: true, message: t('central.handoverCarrier.messages.pleaseInputContactName') as string }]}
        >
          <Input placeholder={t('central.handoverCarrier.placeholders.enterContactName') as string} maxLength={50} showCount />
        </Form.Item>

        <Form.Item
          name="contactPhone"
          label={t('central.handoverCarrier.pageDetails.contactPhone')}
          rules={[
            { required: true, message: t('central.handoverCarrier.messages.pleaseInputContactPhone') as string },
            { pattern: /^\d{10}$/, message: t('central.handoverCarrier.messages.contactPhoneFormat') as string },
          ]}
        >
          <Input placeholder={t('central.handoverCarrier.placeholders.enterContactPhone') as string} maxLength={10} showCount />
        </Form.Item>

        <Form.Item
          name="centerWarehouseIds"
          label={t('central.handoverCarrier.pageDetails.warehouse')}
          rules={[{ required: true, message: t('central.handoverCarrier.messages.pleaseSelectWarehouse') as string }]}
        >
          <Select
            mode="multiple"
            placeholder={t('central.handoverCarrier.placeholders.selectWarehouses') as string}
            options={warehouseOptions}
          />
        </Form.Item>

        <Form.Item
          name="status"
          label={t('central.handoverCarrier.pageDetails.status')}
          initialValue={1}
          rules={[{ required: true, message: t('central.handoverCarrier.messages.pleaseSelectStatus') as string }]}
        >
          <Select
            placeholder={t('central.handoverCarrier.placeholders.selectStatus') as string}
            options={[
              { value: 1, label: t('enum.activeStatus.active') },
              { value: 0, label: t('enum.activeStatus.suspended') },
            ]}
          />
        </Form.Item>
      </Form>
    </EditableDrawer>
  );
};

export default HandoverCarrierFormDrawer;
